import { useState } from "react";
import { ArrowRight, ArrowUpRight, Check, Landmark, ShieldCheck, X } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { EmSwash } from "../components/EmSwash";
import { useNoIndex } from "../lib/useNoIndex";

/**
 * /demo/onboarding-return — mock-up of the "welcome back from Stripe" moment
 * for students, shown before anything is built. Sample data only.
 * Site vocabulary only: serif + swash, the five-gem row, ink/tangerine/grass.
 * No stock illustration, sparkles or mascots.
 */
const Gems = ({ className = "" }) => (
  <div aria-hidden="true" className={`flex items-center gap-2 ${className}`}>
    <span className="h-2 w-6 rounded-full bg-tangerine" />
    <span className="h-2 w-6 rounded-full bg-cobalt" />
    <span className="h-2 w-6 rounded-full bg-grass" />
    <span className="h-2 w-6 rounded-full bg-blossom" />
    <span className="h-2 w-6 rounded-full bg-yellow" />
  </div>
);

const COPY = {
  ready: {
    eyebrow: "Stripe connected",
    title: <>You're <EmSwash>connected.</EmSwash></>,
    body: "Grants from My Rich Aunt can now reach you. When your first grant is sent it lands in your bank account in about two business days, and we'll email you the moment it goes out.",
    cta: "Back to your campaign",
    secondary: "Open Stripe payout dashboard",
  },
  in_review: {
    eyebrow: "Almost there",
    title: <>Stripe is checking a <EmSwash>few details.</EmSwash></>,
    body: "That usually clears within a day, and there's nothing more for you to do. This card updates itself, and grants can be issued the moment it turns green.",
    cta: "Back to your campaign",
    secondary: null,
  },
};

/* A · Letter card that slides in above the payout card */
const LetterCard = ({ state }) => {
  const c = COPY[state];
  const ready = state === "ready";
  return (
    <div data-testid={`return-letter-${state}`} className={`relative overflow-hidden rounded-xl border-2 bg-bone p-6 pr-14 ${ready ? "border-grass" : "border-cobalt"}`}>
      <button type="button" aria-label="Dismiss" className="absolute right-4 top-4 text-olive hover:text-ink"><X className="h-4 w-4" /></button>
      <span className={`text-[10px] font-semibold uppercase tracking-[0.22em] ${ready ? "text-grass" : "text-cobalt"}`}>{c.eyebrow}</span>
      <h3 className="mt-2 font-serif text-3xl leading-[1.05] text-ink lg:text-4xl">{c.title}</h3>
      <p className="mt-3 max-w-xl text-sm leading-relaxed text-olive">{c.body}</p>
      <div className="mt-5 flex flex-wrap items-center gap-4">
        <button type="button" className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-sand hover:bg-forest">{c.cta} <ArrowRight className="h-4 w-4" /></button>
        {c.secondary && <button type="button" className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-olive hover:text-ink">{c.secondary} <ArrowUpRight className="h-3.5 w-3.5" /></button>}
      </div>
      <Gems className="mt-6" />
    </div>
  );
};

/* B · Toast in the corner, the site's own styling instead of the default sonner look */
const Toast = ({ state }) => {
  const c = COPY[state];
  const ready = state === "ready";
  return (
    <div data-testid={`return-toast-${state}`} className="w-[380px] rounded-xl border border-border bg-bone p-5 shadow-[0_24px_60px_-20px_rgba(35,35,32,0.35)]">
      <div className="flex items-start gap-3">
        <span className={`mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sand ${ready ? "bg-grass" : "bg-cobalt"}`}>
          {ready ? <Check className="h-4 w-4" strokeWidth={2.6} /> : <Landmark className="h-4 w-4" strokeWidth={2} />}
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-serif text-xl leading-tight text-ink">{c.title}</p>
          <p className="mt-1.5 text-[13px] leading-relaxed text-olive">{ready ? "Grants from My Rich Aunt can now reach you. We'll email you the moment the first one goes out." : "Usually clears within a day. Nothing more for you to do."}</p>
          <Gems className="mt-3 scale-90 origin-left" />
        </div>
        <button type="button" aria-label="Dismiss" className="text-olive hover:text-ink"><X className="h-4 w-4" /></button>
      </div>
    </div>
  );
};

/* C · Ribbon across the top of the builder */
const Ribbon = ({ state }) => {
  const c = COPY[state];
  const ready = state === "ready";
  return (
    <div data-testid={`return-ribbon-${state}`} className={`flex flex-col gap-3 rounded-xl px-6 py-4 text-sand sm:flex-row sm:items-center sm:justify-between ${ready ? "bg-ink" : "bg-cobalt"}`}>
      <div className="flex items-center gap-4">
        <ShieldCheck className={`h-6 w-6 shrink-0 ${ready ? "text-grass" : "text-sand"}`} strokeWidth={2} />
        <p className="text-sm leading-snug">
          <span className="font-serif text-lg">{ready ? "You're connected." : "Almost there."}</span>{" "}
          <span className="text-sand/80">{ready ? "Grants from My Rich Aunt can now reach you." : "Stripe is checking a few details; this updates on its own."}</span>
        </p>
      </div>
      <button type="button" className="inline-flex items-center gap-2 self-start rounded-full bg-sand px-4 py-2 text-xs font-semibold text-ink hover:bg-yellow sm:self-auto">Got it</button>
    </div>
  );
};

/* The payout card as it looks after a successful return, for context */
const ReadyPayoutCard = () => (
  <div className="rounded-md border border-grass/40 bg-grass/[0.05] p-5">
    <div className="flex items-start gap-4">
      <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-grass text-sand"><ShieldCheck className="h-5 w-5" strokeWidth={2} /></span>
      <div>
        <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-grass">Stripe Connect</span>
        <p className="mt-1.5 font-serif text-xl leading-tight text-ink">Your Stripe account is connected.</p>
        <p className="mt-2 text-sm leading-relaxed text-olive">Grants from My Rich Aunt go directly to your bank account, usually within two business days of being sent.</p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button type="button" className="inline-flex items-center gap-2 rounded-full border-2 border-ink bg-bone px-5 py-2.5 text-sm font-semibold text-ink"><ArrowUpRight className="h-4 w-4" /> Open Stripe payout dashboard</button>
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-olive">Refresh</span>
        </div>
      </div>
    </div>
  </div>
);

const Label = ({ children }) => <p className="text-xs font-semibold uppercase tracking-[0.22em] text-olive">{children}</p>;
const Note = ({ children }) => <p className="mt-2 max-w-2xl text-[13px] italic text-olive">{children}</p>;

export default function OnboardingReturnMockup() {
  useNoIndex();
  const [state, setState] = useState("ready");
  return (
    <main data-testid="onboarding-return-mockup" className="min-h-screen bg-sand text-ink">
      <Navbar onStart={() => {}} primaryLabel="Preview the platform" secondaryLabel={null} />
      <section className="mx-auto max-w-5xl px-6 pb-16 pt-32 lg:px-8 lg:pt-44">
        <span className="inline-flex items-center gap-2 rounded-full bg-cobalt/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-cobalt">Onboarding return · mock-up for review</span>
        <h1 className="mt-4 max-w-3xl font-serif text-5xl leading-[0.95] tracking-tight text-ink lg:text-6xl">The moment a student <EmSwash>comes back from Stripe.</EmSwash></h1>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-olive">
          Today they get a one-line grey toast ("Payouts are set up."). Three warmer treatments below, in the site's own voice and marks: serif, the swash, the five gems. No illustration, no sparkles. Each has two states, because Stripe often keeps verifying for a few hours after the student finishes.
        </p>
        <div className="mt-8 flex items-center gap-2">
          <Label>Preview state:</Label>
          {["ready", "in_review"].map((s) => (
            <button key={s} type="button" onClick={() => setState(s)} data-testid={`return-state-${s}`}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold transition ${state === s ? "bg-ink text-sand" : "bg-bone text-ink ring-1 ring-inset ring-border hover:ring-ink"}`}>
              {s === "ready" ? "Verified on return" : "Still verifying"}
            </button>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20 lg:px-8">
        <Label>A · Letter card (recommended)</Label>
        <Note>Slides in directly above the payout card inside the builder, where the student was when they left. Stays until dismissed, so it's still there if they come back an hour later.</Note>
        <div className="mt-6 space-y-6 rounded-2xl border border-border bg-sand p-6 lg:p-10">
          <LetterCard state={state} />
          <ReadyPayoutCard />
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20 lg:px-8">
        <Label>B · Corner toast, restyled</Label>
        <Note>Same placement as today's toast, but in our type and palette, 8 seconds on screen, then gone. Lightest touch.</Note>
        <div className="relative mt-6 h-[300px] overflow-hidden rounded-2xl border border-border bg-sand">
          <div className="absolute inset-x-8 top-8 space-y-3 opacity-40">
            <div className="h-6 w-2/5 rounded bg-ink/10" /><div className="h-3 w-3/5 rounded bg-ink/10" /><div className="h-3 w-1/2 rounded bg-ink/10" />
            <div className="mt-6 h-40 rounded-md border border-cobalt/30 bg-cobalt/[0.04]" />
          </div>
          <div className="absolute bottom-6 right-6"><Toast state={state} /></div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20 lg:px-8">
        <Label>C · Ribbon across the top of the builder</Label>
        <Note>Impossible to miss, one line, dismisses with "Got it". Best if you'd rather not add another card to the page.</Note>
        <div className="mt-6 space-y-6 rounded-2xl border border-border bg-sand p-6 lg:p-10">
          <Ribbon state={state} />
          <div className="space-y-3 opacity-40"><div className="h-6 w-2/5 rounded bg-ink/10" /><div className="h-3 w-3/5 rounded bg-ink/10" /></div>
          <ReadyPayoutCard />
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-28 lg:px-8">
        <Label>If you say yes, what gets built</Label>
        <ol className="mt-4 max-w-2xl list-decimal space-y-2 pl-5 text-sm leading-relaxed text-ink">
          <li>When the student lands on the dashboard with <code className="rounded bg-bone px-1">?payouts=return</code>, we re-check Stripe and show the chosen treatment in the matching state (verified / still verifying).</li>
          <li>If verification finishes later, the card flips to the "You're connected" copy the next time the status refreshes, so nobody misses the moment.</li>
          <li>The plain grey toast goes away.</li>
        </ol>
      </section>
    </main>
  );
}
